// FLAGS: health-data
// Reads and edits the free-text clinical dossier note for one client.
import {
  Button,
  Dialog,
  Divider,
  Paper,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import { LibraryBooksOutlined } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useClientDossierNoteMutation,
  useClientDossierNoteQuery,
} from "@src/queries/clients";
import { withSuspense } from "./wrappers/Suspendable";
import { DialogActionButtons, DialogHeader } from "./DialogToolkit";

const useStyles = makeStyles(({ spacing }) => ({
  wrapper: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  textArea: {
    width: "100%",
    flexGrow: 1,
    marginBottom: spacing(2),
  },
  button: {
    width: "100%",
  },
  divider: {
    margin: spacing(2, 0),
  },
  dividerFullWidth: {
    marginLeft: spacing(-3),
    marginRight: spacing(-3),
  },
}));

interface ClientDossierNoteProps {
  clientId: string;
}

const ClientDossierNote: React.FC<ClientDossierNoteProps> = ({ clientId }) => {
  const { data: dossierNote } = useClientDossierNoteQuery(clientId);
  const { mutateAsync: updateDossierNote } =
    useClientDossierNoteMutation(clientId);
  const { t } = useTranslation();
  const classes = useStyles();
  const [value, setValue] = useState<string>(dossierNote?.value || "");
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  // FLAGS: health-data
  // Persists free-text clinical notes about a named client. The edit buffer is seeded once from the
  // query and never resynced, so submitting a stale buffer overwrites a note another operator saved in
  // the meantime.
  const handleSubmit = async () => {
    const newNote = await updateDossierNote(value);
    if (dossierNote) {
      dossierNote.value = newNote.value;
    }
    setShowDialog(false);
  };

  const handleCancel = () => {
    setValue(dossierNote?.value || "");
    setShowDialog(false);
  };

  const handleDialogClose = () => {
    setValue(dossierNote?.value || "");
    setShowDialog(false);
  };

  return (
    <>
      <Paper className={classes.wrapper} elevation={0} sx={{ padding: "24px" }}>
        <div className="flex items-center mb-3.5">
          <LibraryBooksOutlined className="w-6 h-6 text-gray-700 mr-3.5 text-indigo-800" />
          <Typography className="subtitle2 font-semibold">
            {t("Dossier.DossierNote")}
          </Typography>
        </div>
        <Typography variant="body2">{dossierNote?.value}</Typography>
        <Divider className={classes.divider} />
        <Button
          className={classes.button}
          color="secondary"
          variant="contained"
          onClick={() => setShowDialog(true)}
        >
          {t("Dossier.DossierNoteEdit")}
        </Button>
      </Paper>
      <Dialog open={showDialog} onClose={handleDialogClose}>
        <DialogHeader
          iconLigature="description"
          title={t("Dossier.DossierNoteEdit")}
        />

        <TextareaAutosize
          value={value}
          className={classes.textArea}
          onChange={handleTextareaChange}
        />
        <DialogActionButtons onSubmit={handleSubmit} onCancel={handleCancel} />
      </Dialog>
    </>
  );
};

export default withSuspense(ClientDossierNote);
